import { ApplicationConfig, importProvidersFrom, inject, Injectable, provideAppInitializer } from '@angular/core';
import { ActivatedRoute, NavigationEnd, provideRouter, Router, RouterModule, Scroll, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { LangChangeEvent, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
// import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeNl from '@angular/common/locales/nl';

import { appconfig } from './config/appconfig';
import { AppConfig } from '@tt-lib/interfaces/app-config';
import { TT_LIB_CONFIG } from '@tt-lib/tt-lib.config.token';
import { TT_LIB_ENV_CONFIG } from '@tt-lib/tt-lib-env.config.token';

import { LanguageService } from '@tt-lib/services/language.service';
import { PermalinkService } from '@tt-lib/services/permalink.service';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { RouteReuseStrategy } from '@angular/router';
import { TmvRouteReuseStrategy } from './tmv-route-reuse-strategy';
import { NgxEchartsModule } from 'ngx-echarts';
// import { provideQuillConfig } from 'ngx-quill/config';
import { NativeHttpInterceptor } from './shared/helpers/nativeHttp.interceptor'
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { Provider } from '@angular/core';

import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { CsrfTokenInterceptor } from './shared/helpers/csrf-token.interceptor';
import { ErrorCatchingInterceptor } from './shared/helpers/error-catching.interceptor';
import { Observable, forkJoin } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';

export function provideTtLibConfig(ttLibConfig: AppConfig): Provider[] {
  return [
    {
      provide: TT_LIB_CONFIG,
      useValue: ttLibConfig
    }
  ];
}

// Registering locale data
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeNl, 'nl');
// Factory for creating the TranslateLoader
// export function createTranslateLoader(http: HttpClient): TranslateLoader {
//   return new TranslateHttpLoader(http, './assets/i18n/', '.json');
// }
// Initializer function for the LanguageService
export function languageServiceInitializer(
  languageService: LanguageService,
  translateService: TranslateService,
  router: Router,
  permalinkService: PermalinkService,
  route: ActivatedRoute,
) {
  return () => {
    return new Promise<void>((resolve) => {
      // Initialize language service
      languageService.init();
      // Listen for language change and then router NavigationEnd event
      translateService.onLangChange.pipe(
        take(1), // Only take the first language change event
        switchMap((event: LangChangeEvent) => {
          //console.log('Language initialized:', event.lang);
          resolve(); // Resolve the promise after initialization
          // Wait for the first NavigationEnd event
          return router.events.pipe(
            filter(routerEvent => routerEvent instanceof NavigationEnd),
            take(1)
          );
        })
      ).subscribe(() => {
        // Perform permalink update on NavigationEnd
        permalinkService.updateLanguage(translateService.currentLang);
        // resolve()  
      });
    });
  };

}

export interface MultiTranslateLoaderConfig {
  prefix: string;
  suffix: string;
}

export class CustomMultiTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private resources: MultiTranslateLoaderConfig[]
  ) { }

  public getTranslation(lang: string): Observable<any> {
    const requests = this.resources.map(resource =>
      this.http.get(`${resource.prefix}${lang}${resource.suffix}`)
    );

    return forkJoin(requests).pipe(
      map(responseArr => {
        return responseArr.reduce((acc, res) => {
          return { ...acc, ...res };
        }, {});
      })
    );
  }
}


// @Injectable({ providedIn: 'root' })
// export class FirstLoadService {
//   public isFirstLoad = true;
//   setFirstLoad() {
//     this.isFirstLoad = false;
//   }
// }

// export function initializeApp(firstLoadService: FirstLoadService) {
//   return () => firstLoadService.markFirstLoad();
// }


export const appConfig: ApplicationConfig = {
  providers: [
    provideTtLibConfig(appconfig),
    { provide: TT_LIB_ENV_CONFIG, useValue: environment },
    // Using provideAppInitializer instead of APP_INITIALIZER
    provideAppInitializer(() => {
      const initializerFn = languageServiceInitializer(
        inject(LanguageService),
        inject(TranslateService),
        inject(Router),
        inject(PermalinkService),
        inject(ActivatedRoute),
      );
      return initializerFn(); // Call the returned function
    }),

    //  provideZoneChangeDetection({ eventCoalescing: true }),

    provideRouter(routes,
      // withHashLocation(),
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),

    provideAnimationsAsync(),

    provideHttpClient(withFetch()),

    provideHttpClient(
      withInterceptorsFromDi()
    ),
    importProvidersFrom(RecaptchaV3Module),  // registers Recaptcha module
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: '6LfLfOQUAAAAALedRcjCT6y3dPqhSx8ki_ckPInZ' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NativeHttpInterceptor,
      multi: true
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: CsrfTokenInterceptor,
    //   multi: true
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorCatchingInterceptor,
      multi: true
    },


    provideServiceWorker('ngsw-worker.js', {
      //enabled: environment.production  && Capacitor.getPlatform() === 'web',
      //android is running under localhost
      enabled: environment.production && window.location.href.indexOf('https://localhost') === -1 && window.location.href.indexOf('https://127.0.0.1') === -1,
      registrationStrategy: 'registerWhenStable:30000',
    }),

    importProvidersFrom(TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        // useFactory: createTranslateLoader,
        useFactory: (http: HttpClient) => new CustomMultiTranslateLoader(http, [
          { prefix: './assets/i18n/common/', suffix: '.json' },
          { prefix: './assets/i18n/', suffix: '.json' }
        ]),
        deps: [HttpClient],
      },
      fallbackLang: 'en',
      lang: 'en'
    })),

    importProvidersFrom(RouterModule.forRoot(routes)), // Provide the router module
    { provide: RouteReuseStrategy, useClass: TmvRouteReuseStrategy },

    importProvidersFrom(
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts')
      })
    ),
    // This turns off all touch interactions globally : MatTooltipDefaultOptions
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
        touchGestures: 'off'
      }
    }

    // provideQuillConfig({
    //   modules: {
    //     syntax: true,
    //     toolbar: [
    //       ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    //       ['blockquote', 'code-block'],

    //       [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    //       [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    //       [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
    //       [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
    //       [{ 'direction': 'rtl' }],                         // text direction

    //       [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    //       [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    //       [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    //       [{ 'font': [] }],
    //       [{ 'align': [] }],

    //       ['clean'],                                         // remove formatting button

    //       ['link', 'image', 'video']                         // link and image, video
    //     ]
    //   }
    // })
  ]
};

// function withScrollPositionRestoration(arg0: string): import("@angular/router").RouterFeatures {
//   throw new Error('Function not implemented.');
// }
